import React, { useState } from "react";
import { Goal } from "../types";
import { createGoal, updateGoal } from "../repository/daysRepository";

interface GoalFormProps {
  existingGoal?: Goal;
  onSaved: () => void;
  onCancel: () => void;
}

const GoalForm: React.FC<GoalFormProps> = ({ existingGoal, onSaved, onCancel }) => {
  const [title, setTitle] = useState(existingGoal?.title || "");
  const [description, setDescription] = useState(existingGoal?.description || "");
  const [goalType, setGoalType] = useState<"binary" | "numeric">(existingGoal?.goal_type || "binary");
  const [target, setTarget] = useState<string>(existingGoal?.target?.toString() || "");
  const [frequencyDays, setFrequencyDays] = useState<string>(existingGoal?.frequency_days?.toString() || "1");
  const [startDate, setStartDate] = useState(existingGoal?.start_date || "");
  const [endDate, setEndDate] = useState(existingGoal?.end_date || "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const data: Record<string, unknown> = {
        title,
        description,
        goal_type: goalType,
        frequency_days: parseInt(frequencyDays) || 1,
      };
      if (goalType === "numeric") {
        data.target = parseFloat(target) || 0;
      }
      if (startDate) data.start_date = startDate;
      if (endDate) data.end_date = endDate;

      if (existingGoal) {
        await updateGoal(existingGoal.uuid, data as Partial<Goal>);
      } else {
        await createGoal(data as Parameters<typeof createGoal>[0]);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save goal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="goal-form" onSubmit={handleSubmit}>
      <h3>{existingGoal ? "Edit Goal" : "New Goal"}</h3>

      {error && <div className="form-error">{error}</div>}

      <div className="form-field">
        <label>Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          placeholder="e.g., Blog, Exercise, Read"
        />
      </div>

      <div className="form-field">
        <label>Description (optional)</label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What does this goal involve?"
        />
      </div>

      <div className="form-row">
        <div className="form-field">
          <label>Type</label>
          <select value={goalType} onChange={(e) => setGoalType(e.target.value as "binary" | "numeric")}>
            <option value="binary">Yes / No</option>
            <option value="numeric">Numeric</option>
          </select>
        </div>

        {goalType === "numeric" && (
          <div className="form-field">
            <label>Daily Target</label>
            <input
              type="number"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              required
              min={0}
              step="any"
            />
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-field">
          <label>Frequency (days)</label>
          <input
            type="number"
            value={frequencyDays}
            onChange={(e) => setFrequencyDays(e.target.value)}
            min={1}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label>Start Date (optional)</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="form-field">
          <label>End Date (optional)</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? "Saving..." : (existingGoal ? "Update" : "Create")}
        </button>
      </div>
    </form>
  );
};

export default GoalForm;
